"use client";

import Link from "next/link";
import { useEffect, useState, type ReactNode } from "react";
import { HiChevronRight } from "@/components/icons";
import BreadCrumb, { type BreadcrumbItem } from "@/components/BreadCrumb";

interface HeaderShellProps {
  breadcrumbs: BreadcrumbItem[];
  isHomePage: boolean;
  /** When true (e.g. a desktop dropdown is open), suppress the breadcrumb so it doesn't visually merge with the dropdown panel below the header. */
  hideBreadcrumb?: boolean;
  children: ReactNode;
}

export default function HeaderShell({
  breadcrumbs,
  isHomePage,
  hideBreadcrumb = false,
  children,
}: HeaderShellProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none ${
        isScrolled ? "pt-4" : "bg-background/95 backdrop-blur-md border-b border-border/60"
      }`}
    >
      <div className="container">
        <div
          className={`flex h-14 items-center justify-between motion-safe:transition-all motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none ${
            isScrolled
              ? "bg-background/95 backdrop-blur-md border border-border/60 rounded-full px-4 shadow-[0_4px_14px_rgba(0,0,0,0.10)] dark:shadow-[0_4px_14px_rgba(0,0,0,0.40)]"
              : ""
          }`}
        >
          {children}
        </div>
      </div>
      {!isHomePage && !isScrolled && !hideBreadcrumb && <BreadCrumb items={breadcrumbs} />}
      {!isHomePage && isScrolled && !hideBreadcrumb && (
        <div className="container pointer-events-none">
          <div className="flex justify-center pt-2">
            <FloatingBreadcrumbPill items={breadcrumbs} />
          </div>
        </div>
      )}
    </header>
  );
}

type CollapsedItem = BreadcrumbItem | { ellipsis: true };

function collapseBreadcrumbs(items: BreadcrumbItem[]): CollapsedItem[] {
  if (items.length <= 4) return items;
  return [items[0], { ellipsis: true }, items[items.length - 2], items[items.length - 1]];
}

function FloatingBreadcrumbPill({ items }: { items: BreadcrumbItem[] }) {
  const collapsed = collapseBreadcrumbs(items);
  return (
    <nav
      aria-label="Breadcrumb"
      className="pointer-events-auto inline-flex max-w-[calc(100vw-48px)] items-center gap-1.5 rounded-full bg-background/95 backdrop-blur-md border border-border/60 shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.35)] px-3 py-1 text-xs text-muted-foreground"
    >
      <ol className="inline-flex items-center gap-1.5 overflow-hidden">
        {collapsed.map((item, index) => {
          const isLast = index === collapsed.length - 1;
          const key = "ellipsis" in item ? `ellipsis-${index}` : `${item.path}-${index}`;
          return (
            <li key={key} className="inline-flex items-center gap-1.5 min-w-0">
              {index > 0 && (
                <HiChevronRight
                  className="h-3 w-3 shrink-0 text-muted-foreground/50"
                  aria-hidden="true"
                />
              )}
              {"ellipsis" in item ? (
                <span className="text-muted-foreground/60" aria-hidden="true">
                  …
                </span>
              ) : isLast ? (
                <span className="font-medium text-foreground max-w-[180px] truncate">
                  {item.name}
                </span>
              ) : (
                <Link
                  href={item.path}
                  className="hover:text-foreground transition-colors max-w-[140px] truncate"
                >
                  {item.name}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
